'use client';

import { Camera, ChevronRight, LogOut, Trash2, X } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { DetailToggle } from '../../molecules/detail-fields/detail-fields';
import { getInitials } from '../../lib/get-initials';

export interface ProfileModalUser {
	/** Nome completo dell'utente. */
	fullName: string | null;
	/** Email dell'utente. */
	email: string | null;
	/** URL dell'avatar, se caricato. */
	avatarUrl: string | null;
	/** Etichetta del ruolo dell'utente nella CER. */
	roleLabel: string;
	/** Nome della CER corrente. */
	cerName: string;
	/** Numero di soci gestiti (per profili admin/referente). */
	/** Soci della CER: solo per chi la amministra; `null` nasconde la riga (INB-377). */
	membersCount: number | null;
	/** Data di primo accesso. */
	createdAt: string | Date | null;
}

/** Consenso a comparire per nome nelle classifiche viste dagli altri soci (INB-357). */
export interface ProfileRankingConsent {
	checked: boolean;
	/** Salvataggio in corso: il controllo resta fermo finché non torna. */
	saving?: boolean;
	onChange: (value: boolean) => void;
}

interface ProfileModalProps {
	/** Se true, il modale è visibile. */
	open: boolean;
	/** Dati del profilo utente da mostrare/modificare. */
	user: ProfileModalUser;
	/** Consenso al nome nelle classifiche: mostrato solo a chi è socio (null → nascosto). */
	rankingConsent?: ProfileRankingConsent | null;
	/** Chiamata alla chiusura del modale. */
	onClose: () => void;
	/** Salva il nuovo nome completo. */
	onSaveName: (name: string) => Promise<void>;
	/** Carica una nuova immagine avatar. */
	onUploadAvatar: (file: File) => Promise<void>;
	/** Rimuove l'immagine avatar corrente. */
	onDeleteAvatar: () => Promise<void>;
	/** Chiamata al click su "Esci". */
	onSignOut: () => void;
	/** Apre la pagina con le preferenze di notifica (assente → riga nascosta). */
	onOpenPreferences?: () => void;
}

function formatDate(dateStr: string | Date | null): string {
	if (!dateStr) return '-';
	const d = new Date(dateStr);
	return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Pannello a scomparsa (dropdown dall'header) con il profilo utente: avatar modificabile, nome modificabile, dati CER/ruolo e logout. */
export function ProfileModal({
	open,
	user,
	rankingConsent,
	onClose,
	onSaveName,
	onUploadAvatar,
	onDeleteAvatar,
	onSignOut,
	onOpenPreferences,
}: ProfileModalProps) {
	const { fullName, email, avatarUrl, roleLabel, cerName, membersCount, createdAt } = user;

	const [editing, setEditing] = useState(false);
	const [draftName, setDraftName] = useState('');
	const [saving, setSaving] = useState(false);
	const [uploadingAvatar, setUploadingAvatar] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	if (!open) return null;

	const displayName = fullName ?? email ?? '-';
	const initials = getInitials(editing ? draftName || null : fullName, email);

	const startEditing = () => {
		setDraftName(fullName ?? '');
		setEditing(true);
	};

	const cancelEditing = () => {
		setEditing(false);
		setDraftName('');
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			await onSaveName(draftName.trim());
			setEditing(false);
			toast.success('Profilo aggiornato');
		} catch {
			toast.error('Errore nel salvataggio del profilo');
		} finally {
			setSaving(false);
		}
	};

	const handleAvatarUpload = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 5 * 1024 * 1024) {
			toast.error('Immagine troppo grande (max 5 MB)');
			return;
		}
		setUploadingAvatar(true);
		try {
			await onUploadAvatar(file);
			toast.success('Immagine profilo aggiornata');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore upload');
		} finally {
			setUploadingAvatar(false);
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	};

	const handleAvatarDelete = async () => {
		setUploadingAvatar(true);
		try {
			await onDeleteAvatar();
			toast.success('Immagine profilo rimossa');
		} catch {
			toast.error('Errore nella rimozione');
		} finally {
			setUploadingAvatar(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[999] flex items-start justify-end pt-[52px] pr-4">
			<button
				type="button"
				aria-label="Chiudi profilo"
				className="absolute inset-0 bg-black/80 cursor-default"
				onClick={onClose}
			/>
			<div className="relative w-[340px] rounded-2xl border border-border bg-panel p-5 shadow-2xl">
				{/* Header */}
				<div className="mb-4 flex items-center justify-between">
					<div className="flex items-center gap-2 text-sm font-bold">
						<span>👤</span>
						Profilo
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Chiudi"
						className="text-text-muted transition-colors hover:text-text-primary"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				{/* User info block */}
				<div className="mb-3 flex items-center gap-3 border-b border-border pb-3">
					{/* Avatar */}
					<div className="relative shrink-0">
						<button
							type="button"
							className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-accent to-accent-purple text-base font-extrabold text-black"
							style={{
								cursor: editing || avatarUrl ? 'pointer' : 'default',
								background: avatarUrl ? 'var(--color-card)' : undefined,
							}}
							onClick={() => {
								if (editing) {
									if (!uploadingAvatar) fileInputRef.current?.click();
								} else if (avatarUrl) {
									window.open(avatarUrl, '_blank');
								}
							}}
							disabled={editing && uploadingAvatar}
							title={
								editing
									? 'Clicca per cambiare immagine'
									: avatarUrl
										? 'Visualizza immagine'
										: undefined
							}
						>
							{avatarUrl ? (
								<img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
							) : uploadingAvatar ? (
								<span className="text-xs">…</span>
							) : (
								initials
							)}
							{/* Camera overlay — only in edit mode */}
							{editing && (
								<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:opacity-100">
									<Camera className="h-4 w-4 text-white" />
								</div>
							)}
						</button>
						{/* Delete button — only in edit mode when avatar exists */}
						{editing && avatarUrl && (
							<button
								type="button"
								onClick={handleAvatarDelete}
								disabled={uploadingAvatar}
								title="Rimuovi immagine profilo"
								className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-danger text-white shadow-md transition-opacity hover:opacity-80 disabled:opacity-40"
							>
								<Trash2 className="h-2.5 w-2.5" />
							</button>
						)}
						<input
							ref={fileInputRef}
							type="file"
							accept="image/jpeg,image/png,image/webp,image/gif"
							className="hidden"
							onChange={handleAvatarUpload}
						/>
					</div>
					<div className="min-w-0 flex-1">
						{editing ? (
							<input
								type="text"
								value={draftName}
								onChange={(e) => setDraftName(e.target.value)}
								placeholder="Nome e cognome"
								className="w-full rounded-lg border border-accent/40 bg-card px-2.5 py-1.5 text-sm font-semibold text-text-primary focus:border-accent focus:outline-none"
							/>
						) : (
							<div className="text-sm font-extrabold">{displayName}</div>
						)}
						<div className="font-mono text-[12px] text-accent-green">{roleLabel} CER</div>
						{email && !editing && (
							<div className="mt-0.5 text-[12px] text-text-muted">
								<a href={`mailto:${email}`} className="transition-colors hover:text-accent">
									{email}
								</a>
							</div>
						)}
					</div>
				</div>

				{/* Info rows */}
				<div>
					<div className="flex items-center justify-between border-b border-border/50 py-2.5">
						<span className="text-[14px] font-semibold">CER</span>
						<span className="font-mono text-[13px] text-accent">{cerName}</span>
					</div>
					<div className="flex items-center justify-between border-b border-border/50 py-2.5">
						<span className="text-[14px] font-semibold">Ruolo</span>
						<span className="font-mono text-[13px] text-accent-green">{roleLabel}</span>
					</div>
					{membersCount != null && (
						<div className="flex items-center justify-between border-b border-border/50 py-2.5">
							<span className="text-[14px] font-semibold">Soci gestiti</span>
							<span className="font-mono text-[13px]">{membersCount}</span>
						</div>
					)}
					<div className="flex items-center justify-between py-2.5">
						<span className="text-[14px] font-semibold">Accesso dal</span>
						<span className="font-mono text-[13px] text-text-muted">{formatDate(createdAt)}</span>
					</div>
					{rankingConsent && (
						<div className="border-t border-border/50 pt-2.5">
							<DetailToggle
								label="Mostra il mio nome nelle classifiche"
								hint="Top Soci e Classifica Community. Senza consenso gli altri soci ti vedono come «socio anonimo»; i tuoi numeri contano comunque."
								checked={rankingConsent.checked}
								disabled={rankingConsent.saving}
								onChange={() => rankingConsent.onChange(!rankingConsent.checked)}
							/>
						</div>
					)}
				</div>

				{onOpenPreferences && !editing && (
					<button
						type="button"
						onClick={onOpenPreferences}
						className="mt-1 flex w-full items-center justify-between border-t border-border/50 py-2.5 text-left transition-colors hover:text-accent"
					>
						<span className="text-[14px] font-semibold">Preferenze notifiche</span>
						<ChevronRight className="h-4 w-4 text-text-muted" />
					</button>
				)}

				{/* Footer */}
				<div className="mt-4 flex items-center justify-between">
					<button
						type="button"
						onClick={onSignOut}
						className="inline-flex items-center gap-1.5 text-[13px] text-text-muted transition-colors hover:text-danger"
					>
						<LogOut className="h-3.5 w-3.5" />
						Esci
					</button>
					<div className="flex gap-2">
						{editing ? (
							<>
								<button
									type="button"
									onClick={cancelEditing}
									disabled={saving}
									className="rounded-[7px] border border-border px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
								>
									Annulla
								</button>
								<button
									type="button"
									onClick={handleSave}
									disabled={saving}
									className="rounded-[7px] bg-accent px-3 py-1.5 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
								>
									{saving ? 'Salvataggio...' : 'Salva'}
								</button>
							</>
						) : (
							<>
								<button
									type="button"
									onClick={onClose}
									className="rounded-[7px] border border-border px-3 py-1.5 text-[13px] text-accent transition-colors hover:border-accent"
								>
									Annulla
								</button>
								<button
									type="button"
									onClick={startEditing}
									className="rounded-[7px] border border-accent/35 bg-accent/[0.08] px-3 py-1.5 text-[13px] font-semibold text-accent transition-colors hover:bg-accent/[0.14]"
								>
									✏️ Modifica
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
